import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  staffContactTypeList,
  staffContactTypeCreate,
  staffContactTypeCreateSuccess,
  staffContactTypeUpdate,
  staffContactTypeUpdateSuccess,
  staffContactTypeDelete,
  staffContactTypeDeleteSuccess,
} from "../../../../store/staffContact/action";

const DEFAULT_BG = "#EEF2FF";
const DEFAULT_TEXT = "#4F46E5";

const EMPTY = {
  Name: "",
  Description: "",
  CssColourBackground: DEFAULT_BG,
  CssColourText: DEFAULT_TEXT,
  Order: "",
  FlagActive: 1,
};

/**
 * Task 24467 - the customisable labels staff contact points can carry.
 *
 * Self-contained rather than threaded through this screen's shared
 * StatusViewEditComponent: that component is driven by activeTab branches in
 * six places, and this lookup carries colours and an active flag the other
 * three do not. Keeping it separate leaves the existing tabs untouched.
 */
export default function StaffContactTypes() {
  const dispatch = useDispatch();

  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const { types, loading, createResp, updateResp, deleteResp, saving } =
    useSelector((state) => ({
      types: state.StaffContact?.staffContactTypeList?.staffcontacttype_list,
      loading: state.StaffContact?.staffContactTypeListLoading,
      createResp: state.StaffContact?.staffContactTypeCreate,
      updateResp: state.StaffContact?.staffContactTypeUpdate,
      deleteResp: state.StaffContact?.staffContactTypeDelete,
      saving:
        state.StaffContact?.staffContactTypeCreateLoading ||
        state.StaffContact?.staffContactTypeUpdateLoading,
    }));

  const reload = () => dispatch(staffContactTypeList());

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const handleSave = () => {
    if (!form.Name.trim()) {
      toast.error("Give the label a name");
      return;
    }
    const row = {
      Name: form.Name.trim(),
      Description: form.Description,
      CssColourBackground: form.CssColourBackground,
      CssColourText: form.CssColourText,
      Order: form.Order === "" ? null : Number(form.Order),
      FlagActive: form.FlagActive ? 1 : 0,
    };
    if (editingId) {
      dispatch(staffContactTypeUpdate([{ Id: editingId, ...row }]));
    } else {
      dispatch(staffContactTypeCreate([row]));
    }
  };

  const startEdit = (t) => {
    setEditingId(t.Id);
    setForm({
      Name: t.Name || "",
      Description: t.Description || "",
      CssColourBackground: t.CssColourBackground || DEFAULT_BG,
      CssColourText: t.CssColourText || DEFAULT_TEXT,
      Order: t.Order ?? "",
      FlagActive: String(t.FlagActive) === "0" ? 0 : 1,
    });
  };

  const settle = (resp, clearAction, okMessage, onOk) => {
    if (isEmpty(resp)) return;
    if (resp.status === 200) {
      toast.success(resp.message || okMessage);
      onOk?.();
      reload();
    } else {
      // The delete endpoint refuses a label still in use and says how many
      // contact points hold it — surface that rather than a generic failure.
      toast.error(resp.message || "Could not save that contact type");
    }
    dispatch(clearAction([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => settle(createResp, staffContactTypeCreateSuccess, "Contact type added", reset), [createResp]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => settle(updateResp, staffContactTypeUpdateSuccess, "Contact type updated", reset), [updateResp]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => settle(deleteResp, staffContactTypeDeleteSuccess, "Contact type deleted", () => setDeleteId(null)), [deleteResp]);

  return (
    <>
      <DeleteModal
        show={!!deleteId}
        onCloseClick={() => setDeleteId(null)}
        onDeleteClick={() => dispatch(staffContactTypeDelete([deleteId]))}
      />

      <InfoCard
        title={editingId ? "Edit contact type" : "Add a contact type"}
        icon="ri-price-tag-3-line"
        accent="indigo"
      >
        <Row className="g-3 align-items-end">
          <Col lg={3}>
            <Label className="mb-1">Name</Label>
            <Input
              value={form.Name}
              onChange={(e) => setForm({ ...form, Name: e.target.value })}
              placeholder="e.g. Personal"
            />
          </Col>
          <Col lg={4}>
            <Label className="mb-1">Description</Label>
            <Input
              value={form.Description}
              onChange={(e) => setForm({ ...form, Description: e.target.value })}
              placeholder="What this label means"
            />
          </Col>
          <Col lg={2}>
            <Label className="mb-1">Order</Label>
            <Input
              type="number"
              value={form.Order}
              onChange={(e) => setForm({ ...form, Order: e.target.value })}
            />
          </Col>
          <Col lg={3}>
            <Label className="mb-1">Chip colour</Label>
            <div className="d-flex align-items-center gap-2">
              <span
                className="badge"
                style={{
                  background: form.CssColourBackground,
                  color: form.CssColourText,
                }}
              >
                {form.Name.trim() || "Label"}
              </span>
              <input
                type="color"
                aria-label="Background colour"
                value={form.CssColourBackground}
                onChange={(e) =>
                  setForm({ ...form, CssColourBackground: e.target.value })
                }
              />
              <input
                type="color"
                aria-label="Text colour"
                value={form.CssColourText}
                onChange={(e) =>
                  setForm({ ...form, CssColourText: e.target.value })
                }
              />
            </div>
          </Col>
          <Col lg={12}>
            <div className="form-check">
              <Input
                type="checkbox"
                className="form-check-input"
                id="contactTypeActive"
                checked={!!form.FlagActive}
                onChange={(e) =>
                  setForm({ ...form, FlagActive: e.target.checked ? 1 : 0 })
                }
              />
              <Label className="form-check-label" for="contactTypeActive">
                Active — inactive labels stay on existing contacts but are not
                offered for new ones
              </Label>
            </div>
          </Col>
          <Col lg={12}>
            <div className="d-flex gap-2">
              <Button color="success" disabled={saving} onClick={handleSave}>
                <i className="ri-save-line align-bottom me-1" />
                {editingId ? "Save changes" : "Add contact type"}
              </Button>
              {editingId && (
                <Button color="light" onClick={reset}>
                  Cancel
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </InfoCard>

      <InfoCard title="Contact types" icon="ri-list-check" accent="indigo">
        {loading ? (
          <LoadingSpinner />
        ) : isEmpty(types) ? (
          <p className="text-muted mb-0">No contact types yet.</p>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-wrap mb-0">
              <thead className="text-muted">
                <tr>
                  <th>Label</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.Id}>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: t.CssColourBackground || DEFAULT_BG,
                          color: t.CssColourText || DEFAULT_TEXT,
                        }}
                      >
                        {t.Name}
                      </span>
                    </td>
                    <td className="text-muted">{t.Description || "—"}</td>
                    <td className="text-muted">{t.Order ?? "—"}</td>
                    <td>
                      {String(t.FlagActive) === "0" ? (
                        <span className="badge bg-secondary-subtle text-secondary">
                          Inactive
                        </span>
                      ) : (
                        <span className="badge bg-success-subtle text-success">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-1"
                        title="Edit"
                        onClick={() => startEdit(t)}
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        title="Delete"
                        onClick={() => setDeleteId(t.Id)}
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </InfoCard>
    </>
  );
}
